import { UserRole } from '@/core/models/user.model';

/**
 * Source unique des rôles autorisés, par page, pour le module Accès (CORA, ASC,
 * Crédit, Paramètres). Partagée entre `sidebar-menu.ts` (visibilité du lien) et
 * les `*.route.ts` (canActivate: [roleGuard(...)]) — une page = une constante,
 * importée des deux côtés, pour éviter que le menu et le guard divergent.
 *
 * Les pages sans entrée de menu dédiée (détails, fiches, drill-down) utilisent
 * l'union des rôles de la page mère (ex: CORA_VIEWERS, ASC_ALL, CREDIT_ALL).
 */

const {
  Admin,
  DG,
  DGA,
  DirectriceExploitation: D_EXPL,
  ResponsableExploitation: RESPO_EXPL,
  ChargeCora: CHARGE_CORA,
  ResponsableFrontOffice: RESPO_FO,
  AdministrationAudit: ADMIN_AUDIT,
  GestionCora: GESTION_CORA,
  AgentBO: AGENT_BO,
  assistanteClientelePME: ASSC_PME,
  agentAccueil: AGENT_ACC,
  ResponsableClientelePME: RESPO_CLT_PME,
  responsableClient: RC,
  ChefAgence: CA,
  ChefAgenceAdjoint: CAA,
  conseilClientele: CC,
  GestionnairePortefeuilles: GP,
  ChefUnitePolyvalent: CUP,
  ChefEquipe: CE,
  AnalysteRisque: AR,
  SuperviseurRisqueZone: SUP_RISQ_ZONE,
  ResponsableRegional: RESPO_RGL,
  SuperviseurPME: SUP_PME,
  ChargeDeveloppementCommercial: CDC,
  AgentCommercialJunior: ACJ,
  DirecteurRisque: DR,
  ChargeDuComite: CHARGE_COMIT,
  Chargedepartementcredit: CDCR,
  ResponsableJuridique: RESPO_JURIDIQUE,
  ResponsableAssurance: RESPO_ASSUR,
  ResponsableClienteleTPE: RESPO_CLT_TPE,
  ResponsableProduitAgricole: RESPO_PROD_AGRI,
  ResponsableReseau: RESPO_RS,
} = UserRole;

// ── CORA ─────────────────────────────────────────────────────────────────────
export const CORA_DASHBOARD_ROLES = [
  Admin,
  DG,
  DGA,
  D_EXPL,
  RESPO_EXPL,
  CHARGE_CORA,
  RESPO_FO,
  ADMIN_AUDIT,
  GESTION_CORA,
] as const;

export const CORA_PENDING_ROLES = [
  Admin,
  DGA,
  D_EXPL,
  RESPO_EXPL,
  GESTION_CORA,
  AGENT_BO,
  CHARGE_CORA,
] as const;

export const CORA_LIST_ROLES = [
  Admin,
  DG,
  DGA,
  D_EXPL,
  RESPO_EXPL,
  AGENT_BO,
  RESPO_FO,
  CHARGE_CORA,
  ADMIN_AUDIT,
] as const;

export const CORA_CREATE_ROLES = [Admin, GESTION_CORA] as const;
export const CORA_MY_CORAS_ROLES = [GESTION_CORA] as const;

/** Détail cora / sous-agent : pas de lien menu dédié — quiconque voit une page cora. */
export const CORA_VIEWERS = [
  ...new Set([...CORA_DASHBOARD_ROLES, ...CORA_PENDING_ROLES, ...CORA_LIST_ROLES]),
] as const;

// ── ASC ──────────────────────────────────────────────────────────────────────
export const ASC_SIEGE_ROLES = [
  Admin,
  ADMIN_AUDIT,
  DG,
  DGA,
  D_EXPL,
  ASSC_PME,
  RESPO_FO,
  AGENT_ACC,
  RESPO_EXPL,
  RESPO_CLT_PME,
] as const;

// CAA ajouté : absent du menu d'origine mais toujours apparié à CA ailleurs (Crédit),
// et déjà autorisé par le guard de route actuel — sans lui, CAA perdait l'accès.
export const ASC_AGENCE_ROLES = [RC, CA, CAA, CC] as const;
export const ASC_CREATE_ROLES = [Admin, RC, CC] as const;

export const ASC_PENDING_ROLES = [
  Admin,
  ADMIN_AUDIT,
  DGA,
  D_EXPL,
  ASSC_PME,
  RC,
  CC,
  RESPO_FO,
  AGENT_ACC,
  RESPO_EXPL,
] as const;

export const ASC_CHEQUES_ROLES = [
  Admin,
  ADMIN_AUDIT,
  DGA,
  D_EXPL,
  ASSC_PME,
  RESPO_EXPL,
  RESPO_FO,
  AGENT_ACC,
  RC,
  CC,
] as const;

// CAA ajouté (voir ASC_AGENCE_ROLES).
export const ASC_LIST_ROLES = [
  Admin,
  ADMIN_AUDIT,
  DG,
  DGA,
  D_EXPL,
  ASSC_PME,
  CA,
  CAA,
  RC,
  CC,
  RESPO_FO,
  AGENT_ACC,
  RESPO_EXPL,
  RESPO_CLT_PME,
] as const;

/** Détail chèque / demande : pas de lien menu dédié — quiconque voit une page ASC. */
export const ASC_ALL = [
  ...new Set([
    ...ASC_SIEGE_ROLES,
    ...ASC_AGENCE_ROLES,
    ...ASC_PENDING_ROLES,
    ...ASC_CHEQUES_ROLES,
    ...ASC_LIST_ROLES,
  ]),
] as const;

// ── CRÉDIT ───────────────────────────────────────────────────────────────────
export const CREDIT_SIEGE_ROLES = [
  Admin,
  DG,
  DGA,
  CDCR,
  DR,
  D_EXPL,
  RESPO_CLT_TPE,
  RESPO_PROD_AGRI,
  ADMIN_AUDIT,
  RESPO_CLT_PME,
  RESPO_EXPL,
  CHARGE_COMIT,
  RESPO_JURIDIQUE,
  RESPO_ASSUR,
  RESPO_FO,
  ASSC_PME,
  RESPO_RS,
] as const;

export const CREDIT_AGENCE_ROLES = [
  CA,
  CAA,
  CC,
  GP,
  CUP,
  CE,
  RC,
  AR,
  SUP_RISQ_ZONE,
  RESPO_RGL,
  SUP_PME,
  CDC,
] as const;

export const CREDIT_CREATE_ROLES = [Admin, ACJ, CE, GP, RC, CC] as const;

export const CREDIT_LIST_ROLES = [
  Admin,
  DG,
  DGA,
  DR,
  CDCR,
  D_EXPL,
  RESPO_EXPL,
  RESPO_FO,
  RESPO_CLT_TPE,
  RESPO_CLT_PME,
  RESPO_PROD_AGRI,
  SUP_PME,
  GP,
  CUP,
  CE,
  ASSC_PME,
  CC,
  CA,
  CAA,
  RC,
  ACJ,
  AR,
  SUP_RISQ_ZONE,
  CHARGE_COMIT,
  RESPO_RGL,
  RESPO_JURIDIQUE,
  RESPO_ASSUR,
] as const;

export const CREDIT_EMPLOYEUR_ROLES = [Admin, CA, CAA, D_EXPL, RC, CC] as const;

/** Tirages découvert : entrée de menu désactivée, rôles repris de CREDIT_CREATE_ROLES. */
export const CREDIT_TIRAGE_ROLES = CREDIT_CREATE_ROLES;

/**
 * Fiche, analyse, résumé, détail agence, organigramme : pas de lien menu dédié
 * (items masqués ou absents) — quiconque voit au moins une page crédit.
 */
export const CREDIT_ALL = [
  ...new Set([
    ...CREDIT_SIEGE_ROLES,
    ...CREDIT_AGENCE_ROLES,
    ...CREDIT_CREATE_ROLES,
    ...CREDIT_LIST_ROLES,
    ...CREDIT_EMPLOYEUR_ROLES,
  ]),
] as const;

// ── ACCESS TRACK ───────────────────────────────────────────────────────────────
export const ACCESS_TRACK_ROLES = [
  Admin,
  DGA,
  DR,
  CDCR,
  D_EXPL,
  RESPO_CLT_PME
] as const;

// ── EXTRACTION ROLES ───────────────────────────────────────────────────────────────
export const EXTRACTION_ROLES = [
  Admin,
  DG,
  DGA,
  DR,
  CDCR,
  D_EXPL,
  ASSC_PME,
  RESPO_CLT_PME,
  RESPO_RGL,
  ADMIN_AUDIT,
] as const;

// ── PARAMÈTRES ───────────────────────────────────────────────────────────────
export const PARAMS_ROLES = [Admin] as const;
